// @givmo/fundraisers-module — the Donors tab (FCM-WEB49; plan §FC-17.3), built to the mockups Jonathan
// ruled on 2026-10-22 09:08 PDT (D-FCM-55): the organizers' private list of one of the organization's
// fundraisers' gifts, over FCM-BE83's `GET /org/{org_ref}/fundraisers/{fundraiser_id}/donors`.
//
// ⚠ EVERY FACT IS THE SERVER'S. A row is one gift, already decided by the one display rule (FCM-BE82): a
// null `name` reads "Redacted", a null `amount` "Amount not shown", `time_shown` false shows the date only,
// `organization` marks an organization's own gift, and `message` comes on named rows only. The order is the
// server's (newest first, on each gift's real time). Nothing here decides who is named, sums a figure or says
// why a row is Redacted; the decode only ever withholds what a row may not carry.
//
// ⚠ A COUNT IS A CLAIM. The api-client's envelope unwrap drops `meta`, so a page knows only its rows and the
// next-page sentinel (`page[limit]` 26 for 25), as the portfolio does; the tab draws no total.
//
// Where it shows is the server's too: the tab exists only while the `/org` detail carries
// `organizer_tools.donor_list` (no web knob). Its own address without it draws `DonorsUnavailable`.

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { extractList, isGivmoError, type OrganizerDonorRowOut } from '@givmo/api-client';
import { orgFundraisersListOrgFundraiserDonorsOptions } from '@givmo/api-client/query';
import {
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { OrgConsoleUnavailableNotice } from '@givmo/org-console';
import { DONORS_COPY as C } from './donors-copy';
import { useFundraisersModule } from './fundraisers-context';
import { formatFundraiserMoney } from './fundraisers-decode';
import { fundraiserOptions } from './fundraisers-queries';
import { FundraisersLoading, FundraisersReadState } from './fundraisers-state';

/** Rows per page. The read asks for one more: the next-page sentinel, since `meta` never arrives. */
export const DONORS_PAGE_SIZE = 25;

/**
 * The zone a row without its time is dated in. The server stamps a Redacted row's day as noon
 * America/New_York on that date (`gift_day`, the employer view's CAM-R159 rule), so the day is read
 * there: in the reader's own zone, a reader at UTC+8 or further east would see the next day.
 */
const DAY_ZONE = 'America/New_York';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
const textOrNull = (value: unknown) =>
  typeof value === 'string' && value.length > 0 ? value : null;

/**
 * The page's rows off the unwrapped list. A body that is not a list is an error, never an empty page (an
 * empty state there would be a claim about a read that did not answer), and so is a row missing what it
 * is keyed and dated by: dropping it would claim a shorter list and move the next-page sentinel. A row
 * never shows more than its name allows: the organization mark, a time and a message come only with a
 * name (D-FCM-38, D-FCM-39), as the server serves them.
 */
export function requireDonorRows(body: unknown): OrganizerDonorRowOut[] {
  if (!Array.isArray(body) && !(isRecord(body) && Array.isArray(body.data))) {
    throw new Error('The donor list could not be read.');
  }
  return extractList<unknown>(body).map((row): OrganizerDonorRowOut => {
    if (!isRecord(row) || typeof row.id !== 'string' || typeof row.given_at !== 'string') {
      throw new Error('The donor list could not be read.');
    }
    const name = textOrNull(row.name);
    return {
      id: row.id,
      name,
      organization: name !== null && row.organization === true,
      amount: textOrNull(row.amount),
      given_at: row.given_at,
      time_shown: name !== null && row.time_shown === true,
      message: name !== null ? textOrNull(row.message) : null,
    };
  });
}

/** One page of the list, under the generated op's own key; the rows are decoded per reader. */
function donorsOptions(orgRef: string, fundraiserId: string, offset: number) {
  return {
    ...orgFundraisersListOrgFundraiserDonorsOptions({
      path: { org_ref: orgRef, fundraiser_id: fundraiserId },
      query: { 'page[limit]': DONORS_PAGE_SIZE + 1, 'page[offset]': offset },
    }),
    select: requireDonorRows,
    // A route miss or a refusal is an ANSWER (the list is dark here, or not this person's), never a
    // blip: no retry. Anything else keeps the hosts' one retry.
    retry: (failureCount: number, error: unknown) =>
      !(isGivmoError(error) && (error.status === 404 || error.status === 403)) && failureCount < 1,
  };
}

/** Whether the `/org` detail lights the tab: `organizer_tools.donor_list`, present only while the list
 *  serves (FCM-BE83). Read defensively although the snapshot types it: only a literal `true` lights it. */
export function donorListServed(row: unknown): boolean {
  return isRecord(row) && isRecord(row.organizer_tools) && row.organizer_tools.donor_list === true;
}

/** "Oct 20, 2026, 1:42 PM" where the row shows its time; "Oct 20, 2026" where it shows the day alone,
 *  read in the zone the server stamps that day in. */
export function donorDate(row: Pick<OrganizerDonorRowOut, 'given_at' | 'time_shown'>): string {
  const date = new Date(row.given_at);
  if (Number.isNaN(date.getTime())) return '—';
  return row.time_shown
    ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
    : new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: DAY_ZONE }).format(date);
}

/** The tab's own address while no list serves for this fundraiser here (the detail lights no list, or
 *  the list answers its route miss): the Layers tab's pattern, with the tab itself hidden. */
export function DonorsUnavailable() {
  return (
    <OrgConsoleUnavailableNotice
      testId="fundraiser-donors-unavailable"
      title={C.tab}
      description={C.unavailable}
    />
  );
}

function DonorName({ row }: { row: OrganizerDonorRowOut }) {
  return (
    <div className="flex min-w-0 flex-col gap-4 py-16">
      {row.name == null ? (
        <span
          className="text-text-secondary"
          {...testIdProp(`fundraiser-donor-${row.id}-redacted`)}
        >
          {C.redacted}
        </span>
      ) : (
        <span className="font-w600 text-text-strong wrap-anywhere">{row.name}</span>
      )}
      {row.organization === true && (
        <span className="text-s12 text-text-secondary">{C.organization}</span>
      )}
    </div>
  );
}

function DonorAmount({ row }: { row: OrganizerDonorRowOut }) {
  return row.amount == null ? (
    <span className="whitespace-nowrap text-s13 text-text-secondary">{C.amountNotShown}</span>
  ) : (
    <span className="whitespace-nowrap tabular-nums text-text-strong">
      {formatFundraiserMoney(row.amount)}
    </span>
  );
}

/** The donor's own words to the organizers, whole and wrapped (decision 6): their line breaks kept. */
function DonorMessage({ text }: { text: string }) {
  return <p className="whitespace-pre-line text-s14 leading-h15 text-text wrap-anywhere">{text}</p>;
}

const COLUMNS: DataTableColumn<OrganizerDonorRowOut>[] = [
  { id: 'donor', header: C.donor, width: '30%', cell: (row) => <DonorName row={row} /> },
  {
    id: 'amount',
    header: C.amount,
    align: 'right',
    numeric: true,
    cell: (row) => (
      <div className="py-16">
        <DonorAmount row={row} />
      </div>
    ),
  },
  {
    id: 'date',
    header: C.date,
    cell: (row) => (
      <div className="whitespace-nowrap py-16 text-text-secondary">{donorDate(row)}</div>
    ),
  },
  {
    id: 'message',
    header: C.message,
    cell: (row) =>
      row.message == null ? null : (
        <div className="py-16">
          <DonorMessage text={row.message} />
        </div>
      ),
  },
];

/**
 * The tab's content: the class's note (host data), then the rows — a table from `lg`, a card per gift
 * below it, as the module's other lists — and Previous · Page · Next under them past one page; or the
 * read's own loading, empty, failed or unavailable state.
 */
export function DonorsTab({ fundraiserId }: { fundraiserId: string }) {
  const config = useFundraisersModule();
  const client = useQueryClient();
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    ...donorsOptions(config.orgRef ?? '', fundraiserId, offset),
    enabled: config.orgRef !== null,
  });
  // The list's route miss means no list serves here now (its flag went dark after the detail lit the
  // tab, or the fundraiser went): the detail is read again, once, so the tab follows the server
  // (D-FCM-55's decision 3: at its own address with no list, the tab is hidden).
  const missing = query.isError && isGivmoError(query.error) && query.error.status === 404;
  useEffect(() => {
    if (!missing) return;
    void client.invalidateQueries({
      queryKey: fundraiserOptions(config.orgRef ?? '', fundraiserId).queryKey,
    });
  }, [missing, client, config.orgRef, fundraiserId]);

  if (query.isError) {
    const error = query.error;
    if (missing) return <DonorsUnavailable />;
    if (isGivmoError(error) && error.status === 403)
      return <FundraisersReadState error={error} onRetry={() => void query.refetch()} />;
    return (
      <EmptyState
        testId="fundraiser-donors-error"
        title={C.failed}
        description={C.failedBody}
        action={
          <Button
            appearance="saas"
            tone="secondary"
            testId="fundraiser-donors-retry"
            onClick={() => void query.refetch()}
          >
            {C.retry}
          </Button>
        }
      />
    );
  }
  if (query.isPending) return <FundraisersLoading />;

  const rows = query.data.slice(0, DONORS_PAGE_SIZE);
  const hasNext = query.data.length > DONORS_PAGE_SIZE;
  return (
    <div className="flex min-w-0 flex-col gap-16" {...testIdProp('fundraiser-donors')}>
      {/* The note explains rows, so it waits for one (decision 4). */}
      {rows.length > 0 && (
        <p className="text-s14 text-text-secondary" {...testIdProp('fundraiser-donors-note')}>
          {config.flavor.donorsNote}
        </p>
      )}
      {rows.length === 0 ? (
        // A later page that comes back empty (gifts gone between reads) makes no claim; its pager
        // still leads back.
        offset === 0 && (
          <Card testId="fundraiser-donors-empty-card">
            <EmptyState testId="fundraiser-donors-empty" title={C.empty} />
          </Card>
        )
      ) : (
        <>
          <div className="hidden lg:block [&_td]:align-top">
            <DataTable
              testId="fundraiser-donors-table"
              columns={COLUMNS}
              rows={rows}
              rowKey={(row) => row.id}
              rowTestId={(row) => `fundraiser-donor-row-${row.id}`}
            />
          </div>
          <div className="flex flex-col gap-12 lg:hidden" {...testIdProp('fundraiser-donor-cards')}>
            {rows.map((row) => (
              <Card key={row.id} flush testId={`fundraiser-donor-card-${row.id}`}>
                <CardBody className="flex flex-col gap-12 p-16">
                  <div className="flex items-start justify-between gap-12">
                    <div className="min-w-0">
                      {row.name == null ? (
                        <p className="text-s16 text-text-secondary">{C.redacted}</p>
                      ) : (
                        <p className="text-s16 font-w600 text-text-strong wrap-anywhere">
                          {row.name}
                        </p>
                      )}
                      {row.organization === true && (
                        <p className="mt-2 text-s12 text-text-secondary">{C.organization}</p>
                      )}
                    </div>
                    <p className="shrink-0 pt-2 text-right text-s14">
                      <DonorAmount row={row} />
                    </p>
                  </div>
                  <p className="text-s13 text-text-secondary">{donorDate(row)}</p>
                  {row.message != null && (
                    <div className="border-t border-border-subtle pt-12">
                      <DonorMessage text={row.message} />
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        </>
      )}
      {(offset > 0 || hasNext) && (
        <div
          className="flex items-center justify-between gap-12"
          {...testIdProp('fundraiser-donors-pagination')}
        >
          <Button
            appearance="saas"
            tone="secondary"
            disabled={offset === 0}
            onClick={() => setOffset((current) => Math.max(0, current - DONORS_PAGE_SIZE))}
            testId="fundraiser-donors-previous"
          >
            {C.previous}
          </Button>
          <span className="text-s13 text-text-secondary">
            {C.page(Math.floor(offset / DONORS_PAGE_SIZE) + 1)}
          </span>
          <Button
            appearance="saas"
            tone="secondary"
            disabled={!hasNext}
            onClick={() => setOffset((current) => current + DONORS_PAGE_SIZE)}
            testId="fundraiser-donors-next"
          >
            {C.next}
          </Button>
        </div>
      )}
    </div>
  );
}
